import React, { useEffect, useRef, useState } from 'react'
import type {
  RawVideoDesc,
  VideoDescData,
  VideoDescriptionProps
} from './VideoDescription.types'
import { generateUniqueId } from '../../lib/generateUniqueId'
import {
  fetchBrightcoveData,
  getVideoResourceUrl
} from '../../lib/fetchBrightcoveData'
import { formatText, formatTime } from '../../lib/formatters'
import './video-description.scss'
import xss from 'xss'

export const VideoDescription: React.FC<VideoDescriptionProps> = ({
  accountId,
  policyKey,
  testId,
  playlistId,
  className,
  videoId
}) => {
  const [videos, setVideos] = useState<VideoDescData[]>([])
  const initializedRef = useRef(false)

  useEffect(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    fetchBrightcoveData<RawVideoDesc & { videos?: RawVideoDesc[] }>(
      accountId,
      policyKey,
      getVideoResourceUrl(videoId, playlistId)
    )
      .then(playlist => {
        const newVideos: VideoDescData[] = []

        const processVideo = (video: RawVideoDesc) => {
          const desc: VideoDescData = {
            ...video,
            uniquePlayerId: generateUniqueId()
          }
          newVideos.push(desc)
        }

        if (Array.isArray(playlist.videos)) {
          playlist.videos.forEach(video => processVideo(video))
        } else {
          processVideo(playlist)
        }

        setVideos(newVideos)
      })
      .catch(error => console.error('Error getVideoInfo', error))
  }, [videoId, playlistId, accountId, policyKey])

  return (
    <div className={className} data-testid={testId || undefined}>
      {videos.map(video => (
        <div
          key={video.uniquePlayerId}
          id={`${video.uniquePlayerId}-text-target`}
          className="video-description-container Content u-textPrimary u-paddingBottom32"
        >
          {video.name && (
            <h3
              className="video-name u-paddingBottom8"
              data-testid={testId ? `${testId}-title` : undefined}
              dangerouslySetInnerHTML={{ __html: xss(formatText(video.name)) }}
            />
          )}
          <p
            className="video-description u-colorNeutral"
            data-testid={testId ? `${testId}-description` : undefined}
          >
            {video.long_description && (
              <>
                <span
                  dangerouslySetInnerHTML={{
                    __html: xss(formatText(video.long_description))
                  }}
                />{' '}
              </>
            )}
            <span
              className="video-duration u-textBold"
              dangerouslySetInnerHTML={{ __html: formatTime(video.duration) }}
            />
          </p>
        </div>
      ))}
    </div>
  )
}
